import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { Avatar, SelectPaginate, SelectPaginateOption } from 'ui';
import { HolderSelectionMode } from 'api';

interface HoldersSelectPropsSingle {
  isMulti: false;
  label?: string;
  value: number | null;
  disabled?: boolean;
  error?: string;
  useEntityId?: boolean;
  onChange: (option: number | null) => void;
  className?: string;
}

interface HoldersSelectPropsMulti {
  isMulti: true;
  label?: string;
  value: number[];
  selectionMode?: HolderSelectionMode;
  showSelectionModeShortcuts?: boolean;
  disabled?: boolean;
  error?: string;
  useEntityId?: boolean;
  onChange: (option: number[] | null) => void;
  onSelectionModeChange?: (mode: HolderSelectionMode) => void;
  className?: string;
}

type HoldersSelectProps = HoldersSelectPropsSingle | HoldersSelectPropsMulti;

type HolderOptionValue = number | HolderSelectionMode;
type HolderSelectOption = SelectPaginateOption<HolderOptionValue> & {
  searchText: string;
};

const HoldersSelect = ({
  isMulti = false,
  value,
  disabled,
  error,
  className,
  label = 'Holder',
  useEntityId = false,
  onChange,
  ...props
}: HoldersSelectProps) => {
  const [params] = useSearchParams();
  const register_id = Number(params.get(REGISTER_ID_SEARCH_PARAM_KEY));

  const { data: registerShareholderList, isLoading: isRegisterShareholderListLoading } =
    useRegisterShareholderList({ register_id }, { disabled: !register_id });

  const registerShareholderListOptions = useMemo(
    () =>
      registerShareholderList?.shareholders?.map(
        ({ id, entity_id, entity: { entity_name }, entity_details, user }) => {
          const entityName =
            'business_name_trustee' in (entity_details?.investor_details || {}) &&
            !!entity_details?.investor_details?.business_name_trustee
              ? `${entity_details?.investor_details?.business_name_trustee} <${entity_name}>`
              : `${entity_name}`;
          const firstName = user?.first_name || entityName;
          const lastName = user?.last_name || '';

          return {
            label: entityName,
            value: useEntityId ? entity_id : id,
            avatar: (
              <Avatar
                withBorder
                size="sm"
                className="border-[#C4C4C4]"
                classNameLetters="text-[12px]"
                firstName={firstName}
                lastName={lastName}
                src={user?.avatar_url || undefined}
              />
            ),
            searchText: `${entityName} ${user?.first_name || ''} ${user?.last_name || ''} ${
              user?.email || ''
            }`
              .trim()
              .toLowerCase(),
          };
        },
      ) || [],
    [registerShareholderList?.shareholders, useEntityId],
  );

  const multiProps = props as Pick<
    HoldersSelectPropsMulti,
    'selectionMode' | 'showSelectionModeShortcuts' | 'onSelectionModeChange'
  >;
  const multiValue = value as number[];
  const multiOnChange = onChange as HoldersSelectPropsMulti['onChange'];
  const selectedSingleValue = useMemo(
    () =>
      registerShareholderListOptions.find(option => option.value === value) as
        | HolderSelectOption
        | undefined,
    [registerShareholderListOptions, value],
  );

  const selectedMultiValue = useMemo(
    () =>
      registerShareholderListOptions.filter(option => multiValue.includes(option.value as number)),
    [multiValue, registerShareholderListOptions],
  );

  const handleLoadOptions = async (
    page: number,
    search?: string,
  ): Promise<HolderSelectOption[]> => {
    if (page > 1) return [];

    const normalizedSearch = search?.trim().toLowerCase();
    if (!normalizedSearch) return registerShareholderListOptions as HolderSelectOption[];

    return (registerShareholderListOptions as HolderSelectOption[]).filter(option =>
      option.searchText.includes(normalizedSearch),
    );
  };

  if (isMulti && multiProps.showSelectionModeShortcuts) {
    const shortcutOptions: HolderSelectOption[] = [
      {
        label: 'All holders',
        value: HolderSelectionMode.AllHolders,
        searchText: 'all holders',
      },
      {
        label: 'All holders with non-zero holdings',
        value: HolderSelectionMode.NonZeroHolders,
        searchText: 'all holders with non-zero holdings non zero holders',
      },
    ];
    const selectionMode = multiProps.selectionMode || HolderSelectionMode.SelectedHolders;
    const selectedValues =
      selectionMode === HolderSelectionMode.SelectedHolders
        ? selectedMultiValue
        : shortcutOptions.filter(option => option.value === selectionMode);

    return (
      <SelectPaginate<HolderOptionValue, true>
        isMulti={true}
        label={label}
        disabled={disabled}
        isLoading={isRegisterShareholderListLoading}
        error={error}
        value={selectedValues}
        className={className}
        isClearable={true}
        onAsync={async (page, search) => {
          const holderOptions = await handleLoadOptions(page, search);
          if (page > 1) return [];
          return [...shortcutOptions, ...holderOptions];
        }}
        onChange={options => {
          const selectedOptions = (options || []) as HolderSelectOption[];
          const lastSelectedValue = selectedOptions.at(-1)?.value;

          if (
            lastSelectedValue === HolderSelectionMode.AllHolders ||
            lastSelectedValue === HolderSelectionMode.NonZeroHolders
          ) {
            multiOnChange([]);
            multiProps.onSelectionModeChange?.(lastSelectedValue);
            return;
          }

          multiProps.onSelectionModeChange?.(HolderSelectionMode.SelectedHolders);
          multiOnChange(selectedOptions.map(option => option.value as number));
        }}
      />
    );
  }

  return (
    <SelectPaginate<number, typeof isMulti>
      isMulti={isMulti}
      label={label}
      disabled={disabled}
      isLoading={isRegisterShareholderListLoading}
      error={error}
      value={(isMulti ? selectedMultiValue : selectedSingleValue || null) as any}
      className={className}
      isClearable={true}
      onAsync={handleLoadOptions as any}
      onChange={options => {
        if (isMulti) {
          onChange(
            ((options || []) as HolderSelectOption[]).map(option => option.value as number) as any,
          );
          return;
        }

        onChange(((options as HolderSelectOption | null)?.value || null) as any);
      }}
    />
  );
};

export default HoldersSelect;
